import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { IoCloseOutline } from 'react-icons/io5'
import { toast } from 'react-toastify'

import { formatBytes } from '../../utils/formatBytes'

const DEFAULT_ALLOWED_EXTENSIONS = [
  '.pdf',
  '.docx',
  '.xlsx',
  '.png',
  '.jpg',
  '.jpeg',
  '.zip',
  '.mp4',
  '.webm',
  '.mov',
  '.avi',
  '.mkv',
]

const MIME_BY_EXTENSION = {
  '.pdf': 'application/pdf',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.zip': 'application/zip',
  '.mp4': 'video/mp4',
  '.webm': 'video/webm',
  '.mov': 'video/quicktime',
  '.avi': 'video/x-msvideo',
  '.mkv': 'video/x-matroska',
}

const getFileExtension = (filename) => {
  const dotIndex = filename.lastIndexOf('.')
  if (dotIndex === -1) return ''
  return filename.slice(dotIndex).toLowerCase()
}

const buildAcceptValue = (extensions) =>
  extensions.flatMap((ext) => [ext, MIME_BY_EXTENSION[ext]].filter(Boolean)).join(',')

const AttachmentUploader = ({
  files,
  onChange,
  maxFiles = 5,
  maxSizeMB = 50,
  allowedExtensions = DEFAULT_ALLOWED_EXTENSIONS,
  hideLabel = false,
}) => {
  const { t } = useTranslation()
  const inputRef = useRef(null)
  const maxSizeBytes = maxSizeMB * 1024 * 1024
  const isMaxReached = files.length >= maxFiles

  const handleFileSelect = (selectedFiles) => {
    if (!selectedFiles || selectedFiles.length === 0) return

    const nextFiles = [...files]

    for (const file of Array.from(selectedFiles)) {
      if (nextFiles.length >= maxFiles) {
        toast.error(t('helpdesk.form.attachments.max_files', { count: maxFiles }))
        break
      }

      const extension = getFileExtension(file.name)

      if (!allowedExtensions.includes(extension)) {
        toast.error(t('helpdesk.form.attachments.invalid_type'))
        continue
      }

      if (file.size > maxSizeBytes) {
        toast.error(t('helpdesk.form.attachments.max_size', { size: maxSizeMB }))
        continue
      }

      const isDuplicate = nextFiles.some((existing) => existing.name === file.name && existing.size === file.size)

      if (isDuplicate) {
        toast.error(t('helpdesk.form.attachments.duplicate'))
        continue
      }

      nextFiles.push(file)
    }

    onChange(nextFiles)

    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const handleRemove = (index) => {
    onChange(files.filter((_, fileIndex) => fileIndex !== index))
  }

  const openFilePicker = () => {
    inputRef.current?.click()
  }

  return (
    <div className="mb-4 last:mb-0">
      {!hideLabel ? (
        <label className="mb-1 block text-left text-sm font-medium text-gray-700">
          {t('helpdesk.form.attachments.label')}
        </label>
      ) : null}
      <p className="mb-3 text-left text-xs text-gray-500">
        {t('helpdesk.form.attachments.hint', {
          count: maxFiles,
          size: maxSizeMB,
          extensions: allowedExtensions.join(', '),
        })}
      </p>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={buildAcceptValue(allowedExtensions)}
        className="hidden"
        onChange={(event) => handleFileSelect(event.target.files)}
        disabled={isMaxReached}
      />

      <button
        type="button"
        onClick={openFilePicker}
        disabled={isMaxReached}
        className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {t('helpdesk.form.attachments.choose')}
      </button>

      {files.length > 0 && (
        <ul className="mt-3 space-y-2">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.size}-${index}`}
              className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-800">{file.name}</p>
                <p className="text-xs text-gray-500">{formatBytes(file.size, { decimals: 1 })}</p>
              </div>
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="shrink-0 rounded-md p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
                aria-label={t('helpdesk.form.attachments.remove')}
              >
                <IoCloseOutline size={18} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default AttachmentUploader
